import React, { useEffect, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import { buildMarksSummary, buildModuleDetailsRows, fetchAmciDesign, fetchGradePoints, printAmciDocument, resolveGradePointByPercentage, resolveStudentPhoto } from "../utils/amciDesign";
import { ADMIN_SESSION_EVENT, getSelectedAdminSession, sessionParam } from "../utils/adminSession";

const API = "/api";
const uploadsBase = "/uploads";

const shellStyle = { display: "grid", gap: "20px" };
const heroStyle = { background: "linear-gradient(135deg, #4e1b24 0%, #7c2d3a 48%, #c2410c 100%)", borderRadius: "28px", padding: "24px", color: "#fff", boxShadow: "0 28px 60px rgba(124, 45, 58, 0.20)" };
const tableWrap = { overflowX: "auto", background: "#fff", borderRadius: "24px", padding: "12px", border: "1px solid rgba(148,163,184,.16)", boxShadow: "0 18px 38px rgba(15,23,42,.08)" };
const tableStyle = { width: "100%", borderCollapse: "collapse" };
const thStyle = { textAlign: "left", padding: "14px 12px", color: "#7c2d3a", borderBottom: "1px solid rgba(148, 163, 184, 0.16)", fontSize: "13px", background: "rgba(255,247,237,0.72)" };
const tdStyle = { padding: "14px 12px", borderBottom: "1px solid rgba(148, 163, 184, 0.12)", color: "#0f172a" };

function fileUrl(fileName) {
  return fileName ? `${uploadsBase}/${fileName}` : "";
}

function useCertifiedStudents() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRows = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/student-applications`, {
        params: { workflow_stage: "certified", status: "approved", session_year: sessionParam(), _ts: Date.now() },
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    const reload = () => loadRows();
    window.addEventListener(ADMIN_SESSION_EVENT, reload);
    return () => window.removeEventListener(ADMIN_SESSION_EVENT, reload);
  }, []);

  return { rows, loading, reload: loadRows };
}

function CertificationDownloadsPage({ title, description, documentType }) {
  const { rows, loading, reload } = useCertifiedStudents();

  const printCertificate = async (row) => {
    const design = await fetchAmciDesign("certificate");
    const marksRes = await axios.get(`${API}/student-applications/${row.id}/marks`).catch(() => ({ data: { rows: [] } }));
    const marksRows = Array.isArray(marksRes.data?.rows) ? marksRes.data.rows : [];
    const summary = buildMarksSummary(marksRows);
    const gradeRows = await fetchGradePoints();
    const gradeInfo = resolveGradePointByPercentage(summary.percentage, gradeRows);
    printAmciDocument("certificate", design, {
      student_name: row.student_name,
      father_name: row.father_name,
      registration_no: row.registration_no,
      dob: row.dob,
      course_name: row.course_name,
      admission_date: row.admission_date,
      course_completion_date: row.course_completion_date,
      study_centre: row.study_centre_name || "Franchise Centre",
      centre_name: row.study_centre_name || "Franchise Centre",
      centre_code: row.study_centre_login || row.centre_code || "",
      centre_address: row.study_centre_address || row.centre_address || row.franchise_address || "",
      session_year: row.session_year,
      certificate_no: row.application_no || row.registration_no,
      issue_date: row.updated_at || "",
      grade: gradeInfo.grade || row.status || "Approved",
      grade_point: gradeInfo.grade_point,
      percentage: `${summary.percentage}%`,
      total_obtained: summary.totalObtained,
      total_full_marks: summary.totalFull,
      verification_qr: row.registration_no || "Verification QR",
      student_photo: resolveStudentPhoto(row),
    });
  };

  const printMarksheet = async (row) => {
    try {
      const res = await axios.get(`${API}/student-applications/${row.id}/marks`);
      const marksRows = Array.isArray(res.data?.rows) ? res.data.rows : [];
      const summary = buildMarksSummary(marksRows);
      const design = await fetchAmciDesign("marksheet");
      const gradeRows = await fetchGradePoints();
      const gradeInfo = resolveGradePointByPercentage(summary.percentage, gradeRows);
      printAmciDocument("marksheet", design, {
        student_name: row.student_name,
        registration_no: row.registration_no,
        dob: row.dob,
        course_name: row.course_name,
        admission_date: row.admission_date,
        course_completion_date: row.course_completion_date,
        study_centre: row.study_centre_name || "Franchise Centre",
        centre_name: row.study_centre_name || "Franchise Centre",
        centre_code: row.study_centre_login || row.centre_code || "",
        module_details: buildModuleDetailsRows(marksRows),
        total_obtained: summary.totalObtained,
        total_full_marks: summary.totalFull,
        total_pass_marks: summary.totalPass,
        obtained_marks: summary.totalObtained,
        theory_obtained_marks: summary.theoryObtained,
        theory_full_marks: summary.theoryFull,
        theory_pass_marks: summary.theoryPass,
        practical_obtained_marks: summary.practicalObtained,
        practical_full_marks: summary.practicalFull,
        practical_pass_marks: summary.practicalPass,
        project_assignment_obtained_marks: summary.projectObtained,
        project_assignment_full_marks: summary.projectFull,
        project_assignment_pass_marks: summary.projectPass,
        viva_obtained_marks: summary.vivaObtained,
        viva_full_marks: summary.vivaFull,
        viva_pass_marks: summary.vivaPass,
        percentage: `${summary.percentage}%`,
        grade: gradeInfo.grade,
        issue_date: row.updated_at || "",
        student_photo: resolveStudentPhoto(row),
      });
    } catch (error) {
      window.alert(error.response?.data?.message || "Marksheet data load failed");
    }
  };

  return (
    <DashboardLayout>
      <div style={shellStyle}>
        <section style={heroStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", alignItems: "center" }}>
            <div>
              <p style={{ margin: 0, fontSize: "12px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.78 }}>Certification Desk</p>
              <h1 style={{ margin: "10px 0 8px", fontSize: "34px", lineHeight: 1.1 }}>{title}</h1>
              <p style={{ margin: 0, color: "rgba(255,255,255,.84)", maxWidth: "760px" }}>Session: {getSelectedAdminSession()} | {description}</p>
            </div>
            <button type="button" className="btn btn-light" onClick={reload} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </section>

        <section style={tableWrap}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Student</th>
                <th style={thStyle}>Registration No</th>
                <th style={thStyle}>Course</th>
                <th style={thStyle}>Study Centre</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.map((row) => (
                <tr key={row.id}>
                  <td style={tdStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <img
                        src={row.photo ? fileUrl(row.photo) : "https://placehold.co/52x52/e2e8f0/475569?text=ST"}
                        alt={row.student_name}
                        style={{ width: "52px", height: "52px", borderRadius: "16px", objectFit: "cover", border: "1px solid #e2e8f0" }}
                      />
                      <div>
                        <div style={{ fontWeight: 700 }}>{row.student_name}</div>
                        <div style={{ color: "#64748b", fontSize: "12px" }}>{row.application_no || "-"}</div>
                      </div>
                    </div>
                  </td>
                  <td style={tdStyle}>{row.registration_no || "-"}</td>
                  <td style={tdStyle}>{row.course_name || "-"}</td>
                  <td style={tdStyle}>{row.study_centre_name || "-"}</td>
                  <td style={tdStyle}>
                    <button type="button" className="btn btn-sm btn-primary" onClick={() => (documentType === "marksheet" ? printMarksheet(row) : printCertificate(row))}>
                      {documentType === "marksheet" ? "Download Marksheet" : "Download Certificate"}
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 ? (
                <tr>
                  <td style={tdStyle} colSpan={5}>No approved certified student available.</td>
                </tr>
              ) : null}
              {loading ? (
                <tr>
                  <td style={tdStyle} colSpan={5}>Loading certified students...</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </section>
      </div>
    </DashboardLayout>
  );
}

export function AdminMarksheetDownloads() {
  return <CertificationDownloadsPage title="Marksheet Download" description="Approved exam-complete students move here. Use the action button to print the marksheet." documentType="marksheet" />;
}

export function AdminCertificateDownloads() {
  return <CertificationDownloadsPage title="Certificate Download" description="Approved exam-complete students move here. Use the action button to print the certificate." documentType="certificate" />;
}
